import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";
import { UPGRADE_FLOW_SOURCES } from "@circle-react-shared/UpgradePlan/constants";
import { usePlanRestrictions } from "@circle-react/components/Spaces/NewV3/hooks/usePlanRestrictions";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import { useSpacesLimitAddonModal } from "@/react/components/Spaces/NewV2/NewForm/SpacesLimitAddonModal";
import { getNextTier, type PlanTiers } from "@/react/helpers/planHelpers";

interface UseSpaceLimitErrorHandlerOptions {
  onBeforeModal?: () => void;
  onUpgradeSuccess?: () => Promise<void> | void;
}

interface SpaceLimitApiError {
  body?: {
    error_details?: {
      max_space_limit?: boolean;
    };
    message?: string;
  };
}

export const useSpaceLimitErrorHandler = ({
  onBeforeModal,
  onUpgradeSuccess,
}: UseSpaceLimitErrorHandlerOptions = {}) => {
  const upgradePlanModal = useUpgradePlanModal();
  const spacesLimitAddonModal = useSpacesLimitAddonModal();
  const {
    currentPlanTier,
    isLegibleForUpgradePlanModal,
    isLegibleForAddonModal,
  } = usePlanRestrictions();

  const isSpaceLimitModalAvailable =
    isLegibleForUpgradePlanModal || isLegibleForAddonModal;

  const handleSpaceLimitError = useCallback(
    async (apiError: SpaceLimitApiError) => {
      const isMaxSpaceLimitReached = Boolean(
        apiError.body?.error_details?.max_space_limit,
      );

      if (!isMaxSpaceLimitReached) return false;

      if (isLegibleForUpgradePlanModal) {
        onBeforeModal?.();
        await upgradePlanModal.show({
          planTier: currentPlanTier
            ? (getNextTier(currentPlanTier as PlanTiers) ?? undefined)
            : undefined,
          onSuccess: async () => {
            await onUpgradeSuccess?.();
            await upgradePlanModal.hide();
          },
          subheading: apiError.body?.message,
          successButtonLabel: t(
            "pricing_plans.upgrade_modal.actions.go_to_spaces",
          ),
          source: UPGRADE_FLOW_SOURCES.SPACE_LIMIT_UPGRADE_MODAL,
          showPlanDetails: true,
        });
      } else if (isLegibleForAddonModal) {
        onBeforeModal?.();
        await spacesLimitAddonModal.show({});
      } else {
        return false;
      }

      return true;
    },
    [
      currentPlanTier,
      isLegibleForAddonModal,
      isLegibleForUpgradePlanModal,
      onBeforeModal,
      onUpgradeSuccess,
      spacesLimitAddonModal,
      upgradePlanModal,
    ],
  );

  return { handleSpaceLimitError, isSpaceLimitModalAvailable };
};
